import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import { Lock as LockIcon, Edit as EditIcon } from "@mui/icons-material";
import type { EditFieldGovernance, VendorProfileDocument } from "../types";
import { formatText } from "../utils/format";
import { isRowLocked, lockTooltip } from "./governanceLabels";
import { AttachmentDisplay } from "../components/AttachmentDisplay";

type Props = {
  label: string;
  fieldName: string;
  value: unknown;
  governance: EditFieldGovernance | undefined;
  hasPendingChange?: boolean;
  attachment?: VendorProfileDocument;
  isAttachment?: boolean;
  isInternal?: boolean;
  panStatus?: string | null;
  readOnly?: boolean;
  onEdit?: () => void;
};

export function FieldEditRow({
  label,
  fieldName,
  value,
  governance,
  hasPendingChange,
  attachment,
  isAttachment,
  isInternal = false,
  panStatus,
  readOnly = false,
  onEdit,
}: Props) {
  const governanceLocked = isRowLocked(governance, value, fieldName, isInternal, {
    panStatus,
  });
  const locked = governanceLocked || Boolean(hasPendingChange);
  const displayValue =
    typeof value === "number"
      ? String(value)
      : formatText(value as string | null | undefined);
  const showAttachment = Boolean(isAttachment || attachment);
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: readOnly ? "260px 1fr auto" : "260px 1fr auto 96px",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: "#E0E4EF",
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Box sx={{ color: "text.secondary", fontSize: 13 }}>{label}</Box>
      <Box
        sx={{
          fontSize: 13,
          fontWeight: 500,
          minWidth: 0,
          wordBreak: "break-word",
        }}
      >
        {showAttachment ? (
          <AttachmentDisplay document={attachment} />
        ) : (
          displayValue
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        {hasPendingChange ? (
          <Chip
            size="small"
            label="PENDING"
            sx={{
              bgcolor: "#FFF3D6",
              color: "#B45309",
              fontWeight: 700,
              letterSpacing: 0.3,
              height: 22,
            }}
          />
        ) : null}
      </Box>
      {!readOnly && (
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        {locked ? (
          <Tooltip
            title={
              hasPendingChange && !governanceLocked
                ? "A change request for this field is already pending — wait for it to be reviewed before editing again."
                : lockTooltip(governance, fieldName, { isInternal, panStatus })
            }
          >
            <Box
              component="span"
              tabIndex={0}
              aria-label={`${label} is locked`}
              sx={{
                display: "inline-flex",
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  borderRadius: 0.5,
                },
              }}
            >
              <LockIcon sx={{ fontSize: 18, color: "text.disabled" }} />
            </Box>
          </Tooltip>
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
            onClick={onEdit}
            aria-label={`Edit ${label}`}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Edit</Typography>
          </Button>
        )}
      </Box>
      )}
    </Box>
  );
}

export default FieldEditRow;
